import {
    type FC,
    type RefObject,
    useEffect,
    useRef,
    useMemo,
    type MouseEvent,
    type ReactElement,
} from 'react';
import { graphql, useFragment } from 'react-relay';

// components
import { PdpRespSimilarItemCarousel } from './PdpRespSimilarItemCarousel/PdpRespSimilarItemCarousel.tsx';
import { PdpRespSimilarItemsContainer } from './PdpRespSimilarItemsContainer/PdpRespSimilarItemsContainer.tsx';
import { viewSimilarItems, ItemsSimilarToHeader } from '../pdpMessages.tsx';
import { PdpSharedUnpurchasableHeader } from '../PdpSharedUnpurchasableIcons/PdpSharedUnpurchasableHeader.tsx';

import { useClientState } from 'dibs-react-hooks/exports/useClientState.ts';
import {
    VisibilityTracker,
    type VisibilityChangeCallback,
} from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';
import { type FavoritesProviderChildrenProps } from '../../components/global/FavoriteIcons/FavoritesProviderChildrenProps.ts';
import { FavoritesProvider } from '../../components/global/FavoriteIcons/FavoritesProvider.tsx';

import { locations } from '../../utils/tracking/shared/favoritesTracking.tsx';

// helpers
import classnames from 'classnames';
import scrollIntoView from 'scroll-into-view';
import {
    trackItemClick,
    trackEcommerceLandingItemClick,
    trackLandingViewAllClick,
    trackLandingItemImpressions,
    trackItemsDisplayed,
} from '../tracking/similarItemsTracking.js';
import { PDP_TOP_SIMILAR_ITEMS_COUNT } from '../../constants/pdpConstants.ts';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy.ts';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls.ts';

import { type PdpRespSimilarItemsLanding_item$key } from './__generated__/PdpRespSimilarItemsLanding_item.graphql.ts';
import {
    type PdpRespSimilarItemsLanding_similarItems$data,
    type PdpRespSimilarItemsLanding_similarItems$key,
} from './__generated__/PdpRespSimilarItemsLanding_similarItems.graphql.ts';
import { type PdpRespSimilarItemsLanding_user$key } from './__generated__/PdpRespSimilarItemsLanding_user.graphql.ts';

// styles
import styles from './PdpRespSimilarItemsLanding.scss';

// types
type PdpRespSimilarItemsLandingComponentProps = {
    item: PdpRespSimilarItemsLanding_item$key;
    similarItems: PdpRespSimilarItemsLanding_similarItems$key | null | undefined;
    user: PdpRespSimilarItemsLanding_user$key | null | undefined;
    scrollToRef: RefObject<HTMLElement | null>;
    landingItemSize?: string;
};

type LandingItems = NonNullable<
    NonNullable<PdpRespSimilarItemsLanding_similarItems$data['edges']>[number]
>;

// GraphQL Fragments
const userFragment = graphql`
    fragment PdpRespSimilarItemsLanding_user on User {
        serviceId
    }
`;

const itemFragment = graphql`
    fragment PdpRespSimilarItemsLanding_item on Item {
        title
        isSold
        isListingNotListed
        isListingUnlisted
        seller {
            isInternal
            isAccessible
        }
        ...PdpSharedUnpurchasableHeader_item
        ...PdpRespSimilarItemsContainer_item
    }
`;

const similarItemsFragment = graphql`
    fragment PdpRespSimilarItemsLanding_similarItems on moreLikeThisItemConnection {
        edges {
            node {
                serviceId
                categoryPath
                categoryCode
                browseUrl
                title
                ecommerceTrackingParams
                ...PdpRespSimilarItemCarousel_item
                ...FavoritesProvider_item
            }
        }
    }
`;

export const PdpRespSimilarItemsLanding: FC<PdpRespSimilarItemsLandingComponentProps> = ({
    item: itemRef,
    similarItems: similarItemsRef,
    user: userRef,
    scrollToRef,
    landingItemSize,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const similarItems = useFragment(similarItemsFragment, similarItemsRef);
    const user = useFragment(userFragment, userRef);

    const sectionVisibilityRef = useRef(null);
    const isClient = useClientState();
    const hasFiredImpressions = useRef(false);

    const handleSectionVisibilityChange = ({
        isVisible,
    }: Pick<VisibilityChangeCallback, 'isVisible'>): void => {
        if (isVisible) {
            trackItemsDisplayed({ moduleType: 'PDP_SIMILAR_ITEMS' });
        }
    };

    const scrollToPdpRespSimilarItemsGrid = (): void => {
        const gridEl = scrollToRef?.current;
        if (gridEl) {
            scrollIntoView(gridEl, { time: 500, align: { top: 0, topOffset: 100 } }, () =>
                gridEl.focus()
            );
        }
    };

    const { isListingNotListed, isListingUnlisted, isSold, seller } = item || {};
    const { isAccessible: isSellerAccessible, isInternal: isSellerInternal } = seller || {};

    const isItemListingNotListed = isListingNotListed && !isSellerInternal;
    const isPdpTypeDisplayUnpurchasable =
        ((isListingUnlisted || isSold) && !isItemListingNotListed) || !isSellerAccessible;

    const landingItems = useMemo<LandingItems[]>(() => {
        const items = similarItems?.edges ? similarItems.edges.filter(filterNulls) : [];
        return isPdpTypeDisplayUnpurchasable ? items : items.slice(0, PDP_TOP_SIMILAR_ITEMS_COUNT);
    }, [similarItems, isPdpTypeDisplayUnpurchasable]);

    const onClickItem = (index: number, event: MouseEvent | null = null): void => {
        trackItemClick({ moduleType: 'PDP_SIMILAR_ITEMS' }, event);
        trackEcommerceLandingItemClick({
            index,
            landingItem: landingItems[index],
            isSoldPdp: isPdpTypeDisplayUnpurchasable,
        });
    };

    const onClickViewMore = (event?: MouseEvent | null): void => {
        scrollToPdpRespSimilarItemsGrid();
        // fire tracking
        trackLandingViewAllClick(event);
    };

    const ecommerceTrackingParams = landingItems[0]?.node?.ecommerceTrackingParams;
    useEffect(() => {
        if (!hasFiredImpressions.current && ecommerceTrackingParams) {
            if (landingItems.length) {
                trackLandingItemImpressions(landingItems, isPdpTypeDisplayUnpurchasable);
                hasFiredImpressions.current = true;
            }
        }
    }, [
        hasFiredImpressions,
        ecommerceTrackingParams,
        landingItems,
        isPdpTypeDisplayUnpurchasable,
        item,
    ]);

    let initialLandingItems = landingItems;
    let additionalLandingItems: typeof landingItems | undefined;

    let header: ReactElement | undefined = undefined;
    let subtitle: ReactElement;
    let isHorizontal = false;

    if (initialLandingItems?.length === 0) {
        return null;
    }

    if (isPdpTypeDisplayUnpurchasable) {
        // Unpurchasable Pdp
        header = <PdpSharedUnpurchasableHeader item={item} />;
        subtitle = viewSimilarItems;
    } else {
        // GROWTH-1429: Test Similar Items on Pdp
        subtitle = <ItemsSimilarToHeader title={<i>{item.title}</i>} />;
        isHorizontal = true;
    }

    if (initialLandingItems.length > PDP_TOP_SIMILAR_ITEMS_COUNT) {
        additionalLandingItems = initialLandingItems.slice(PDP_TOP_SIMILAR_ITEMS_COUNT);
        initialLandingItems = initialLandingItems.slice(0, PDP_TOP_SIMILAR_ITEMS_COUNT);
    }

    const landingItemsClassNames = classnames(styles.landingItems, 'rowFlex', {
        [styles.isHorizontal]: isHorizontal,
    });

    const landingItemClassNames = classnames('colLg2', 'colMd4', 'colXs6', styles.landingItem, {
        [styles.smallLandingItem]: landingItemSize === 'small',
    });

    return (
        <div ref={sectionVisibilityRef}>
            <VisibilityTracker
                elementRef={sectionVisibilityRef}
                onVisibilityChange={handleSectionVisibilityChange}
            />
            <FavoritesProvider
                location={locations.SIMILAR_ITEM_TOP}
                disable={!isClient}
                userId={user?.serviceId || null}
                items={similarItems?.edges?.map(edge => edge?.node).filter(filterFalsy) || []}
                fetchFolder={false}
            >
                {({ props: favorites }: { props: FavoritesProviderChildrenProps }) => (
                    <PdpRespSimilarItemsContainer
                        isDark
                        isTopContainer
                        header={header}
                        subtitle={subtitle}
                        onClickViewMore={onClickViewMore}
                        dataTn="pdp-similar-landing-grid"
                        isHorizontal={isHorizontal}
                        item={item}
                    >
                        {/* Grid of Items */}
                        {!initialLandingItems.length ? null : (
                            <>
                                <div className={landingItemsClassNames}>
                                    {initialLandingItems.map(({ node: landingItem }, index) => (
                                        <div
                                            key={landingItem?.serviceId}
                                            className={landingItemClassNames}
                                        >
                                            <PdpRespSimilarItemCarousel
                                                item={landingItem}
                                                index={index}
                                                favorites={favorites}
                                                onClick={onClickItem}
                                                isCondensed={isHorizontal}
                                            />
                                        </div>
                                    ))}
                                </div>
                                {additionalLandingItems ? (
                                    <div className={landingItemsClassNames}>
                                        {additionalLandingItems.map(
                                            ({ node: landingItem }, index) => (
                                                <div
                                                    key={landingItem?.serviceId}
                                                    className={landingItemClassNames}
                                                >
                                                    <PdpRespSimilarItemCarousel
                                                        item={landingItem}
                                                        index={index + PDP_TOP_SIMILAR_ITEMS_COUNT}
                                                        favorites={favorites}
                                                        onClick={onClickItem}
                                                        isCondensed={isHorizontal}
                                                    />
                                                </div>
                                            )
                                        )}
                                    </div>
                                ) : null}
                            </>
                        )}
                    </PdpRespSimilarItemsContainer>
                )}
            </FavoritesProvider>
        </div>
    );
};
